import type { TowerDesign } from "@/game/towerSpec";

/**
 * Seymour 280 ft self-support tower (CT5633 "Seymour East", 6 Progress Ave,
 * Seymour CT / AT&T), from the stamped Maser Consulting tnxTower report in
 * reference_towers/02_engineering_drawings/CT_Seymour_Progress_280ft_selfsupport_structural.pdf
 * (E-1 section schedule, read from the drawing's text layer + high-DPI crop).
 *
 * EXACT data transcribed from the E-1 schedule:
 *   - Triangular (3-legged, △) self-support, 280 ft (85.34 m), 16 sections.
 *   - Section breaks (ft): 0/20/40/60/80/100/120/140/160/180/200/220/230/250/270/280.
 *   - FACE WIDTH taper: 28 ft (base) then −2 ft/20-ft section (28,26,24,22,20,18,
 *     16,14,12,10,8,~6) down to the ~4.5 ft straight pole-top mast (230-280 ft).
 *   - LEGS: pipe, Grade A572-50. Pirod part numbers bottom→top: 112740, 112745,
 *     112744, 112743, 105220, 105219, 105218, 105245 (+ SR mast). Exact pipe
 *     dimensions need the Pirod catalog (not in repo) → approximated by pipe tier,
 *     part numbers recorded here verbatim.
 *   - DIAGONALS: angle, Grade A36, bottom→top: 2L3½×3½×5⁄16×3⁄8 (double angle),
 *     L3½×3½×5⁄16, L4×4×¼, L3×3×5⁄16, L3×3×3⁄16.
 *   - Total steel weight 76.7 kip ≈ 34.8 t.
 *   - Design: TIA-222-G, Exp B, 125 mph ult (97 nominal), Structure Class II,
 *     0.75" ice, 50 mph ice wind. Tower util 63.2%, foundation 63.5% (diagonals
 *     govern 59.8% at 0-20 ft, anchor rods 27.9%). Foundation geometry lives in
 *     externally-referenced Valmont/Destek reports (not in this PDF).
 *
 * Built with the multi-`segments` skeleton so the real per-band face-width taper,
 * member sizes and grades are captured. Legs mapped to the nearest catalog pipe
 * (exact Pirod dims unavailable); diagonals to the nearest catalog angle.
 */

// face width (ft) → triangular circumradius (m) = ft·0.3048/√3
const R = (faceFt: number) => (faceFt * 0.3048) / Math.sqrt(3);

export const design: TowerDesign = {
  id: "selfsupport-seymour-280ft",
  name: "Self-Support (Seymour 280 ft)",
  description: "Real 280 ft triangular self-support tower, Seymour CT (AT&T CT5633, TIA-222-G).",
  author: "reference: CT Seymour Progress 280 ft Maser tnxTower SA",
  spec: {
    type: "lattice",
    height: 85.34, // 280 ft
    segments: {
      faces: 3, // triangular (△ on the drawing)
      baseRadius: R(28), // 28 ft base face → 4.93 m circumradius
      sections: [
        // 0–60 ft: biggest legs (Pirod 112740/112745/112744), double-angle diagonals
        {
          toHeight: 18.29, topRadius: R(22), panels: 3,
          legSection: "legXXL", braceSection: "angleL6",
          legMaterial: "a572", braceMaterial: "a36", bracing: "X",
        },
        // 60–120 ft (Pirod 112743/105220; L3½×3½×5⁄16)
        {
          toHeight: 36.58, topRadius: R(16), panels: 3,
          legSection: "legXL", braceSection: "angleL5",
          legMaterial: "a572", braceMaterial: "a36", bracing: "X",
        },
        // 120–180 ft (Pirod 105220/105219; L4×4×¼)
        {
          toHeight: 54.86, topRadius: R(10), panels: 3,
          legSection: "legXL", braceSection: "braceHeavy",
          legMaterial: "a572", braceMaterial: "a36", bracing: "X",
        },
        // 180–220 ft (Pirod 105218; L3×3×5⁄16)
        {
          toHeight: 67.06, topRadius: R(6), panels: 2,
          legSection: "legHeavy", braceSection: "braceMed",
          legMaterial: "a572", braceMaterial: "a36", bracing: "X",
        },
        // 220–280 ft straight pole-top mast (Pirod 105245 / SR; L3×3×3⁄16)
        {
          toHeight: 85.34, topRadius: R(4.5), panels: 3,
          legSection: "legHeavy", braceSection: "braceMed",
          legMaterial: "a572", braceMaterial: "a36", bracing: "X",
        },
      ],
    },
    foundation: {
      soil: "dense-sand", // New Haven County glacial till (foundation util 63.5%)
      base: { type: "pad", width: 4.0, diameter: 1, depth: 3.0, concrete: "c30" }, // per-leg spread footing (geometry not in this PDF)
      connection: {
        kind: "anchorBolts", nBolts: 8, bolt: "b1_75", grade: "f1554_55",
        boltCircleDia: 0.8, embedment: 0.9, edgeDistance: 0.4,
        platePlanArea: 0.6, plateThickness: 0.05, plateGrade: "a36rod", fc: 30e6,
      },
    },
  },
};
